import * as Sentry from '@sentry/react'
import { Link } from '@tanstack/react-router'
import { AlertTriangle } from 'lucide-react'
import posthog from 'posthog-js'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { env } from './config/env'

type Props = {
  error: Error & { digest?: string }
  reset?(): void
}

const ErrorPage = ({ error, reset }: Props) => {
  const { t } = useTranslation()

  useEffect(() => {
    Sentry.captureException(error)
    if (env.posthog.key && env.posthog.host) {
      posthog.captureException(error)
    }
  }, [error])

  const onClickReset = () => {
    localStorage.clear()
    if (reset) reset()
    else window.location.reload()
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center gap-8 px-4 text-center">
      <div className="flex flex-col items-center gap-4">
        <div className="rounded-full bg-red-100 p-4 dark:bg-red-900/20">
          <AlertTriangle className="h-12 w-12 text-red-600 dark:text-red-500" />
        </div>
        <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl">{t('errorPage.title')}</h1>
        <p className="max-w-150 text-gray-500 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed dark:text-gray-400">
          {t('errorPage.description')}
        </p>
        {error.digest && <p className="text-xs text-gray-400 font-mono">{error.digest}</p>}
      </div>
      <div className="flex gap-4">
        <Button asChild variant="secondary">
          <Link to="/">{t('errorPage.goHomeButton')}</Link>
        </Button>
        <Button onClick={onClickReset} variant="default">
          {t('errorPage.refreshPageButton')}
        </Button>
      </div>
    </div>
  )
}

export default ErrorPage
